import { inject, Injectable } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Department, Location, OrganizationDetails, Tenant, TenantSettings } from '../models';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) {
      throw new Error('No tenant selected');
    }
    return id;
  }

  async getTenant(): Promise<Tenant | null> {
    const tenantRef = doc(this.firestore, `tenants/${this.tenantId}`);
    const snap = await getDoc(tenantRef);

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'],
      domain: data['domain'],
      settings: data['settings'],
      subscription: data['subscription'],
      createdAt: data['createdAt']?.toDate(),
      updatedAt: data['updatedAt']?.toDate(),
    };
  }

  async updateSettings(settings: Partial<TenantSettings>): Promise<void> {
    const tenantRef = doc(this.firestore, `tenants/${this.tenantId}`);
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    for (const [key, value] of Object.entries(settings)) {
      if (value === undefined) {
        continue;
      }
      updates[`settings.${key}`] = value;
    }
    await updateDoc(tenantRef, updates);

    const tenant = this.authStore.tenant();
    if (tenant) {
      this.authStore.setTenant({
        ...tenant,
        settings: {
          ...(tenant.settings ?? {}),
          ...settings,
        },
      });
    }
  }

  async updateOrganization(details: OrganizationDetails): Promise<void> {
    const tenantRef = doc(this.firestore, `tenants/${this.tenantId}`);
    await updateDoc(tenantRef, {
      name: details.name,
      'settings.organization': details,
      updatedAt: serverTimestamp(),
    });

    const tenant = this.authStore.tenant();
    if (tenant) {
      this.authStore.setTenant({
        ...tenant,
        name: details.name,
        settings: {
          ...(tenant.settings ?? {}),
          organization: details,
        },
      });
    }
  }

  async getDepartments(): Promise<Department[]> {
    const deptCollection = collection(
      this.firestore,
      `tenants/${this.tenantId}/departments`
    );
    const snap = await getDocs(deptCollection);

    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data['name'],
        code: data['code'],
        parentDepartmentId: data['parentDepartmentId'] ?? null,
        headId: data['headId'] ?? null,
        createdAt: data['createdAt']?.toDate(),
      };
    });
  }

  async getDepartment(departmentId: string): Promise<Department | null> {
    const deptRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/departments/${departmentId}`
    );
    const snap = await getDoc(deptRef);

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'],
      code: data['code'],
      parentDepartmentId: data['parentDepartmentId'] ?? null,
      headId: data['headId'] ?? null,
      createdAt: data['createdAt']?.toDate(),
    };
  }

  async getLocations(): Promise<Location[]> {
    const locCollection = collection(
      this.firestore,
      `tenants/${this.tenantId}/locations`
    );
    const snap = await getDocs(locCollection);

    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data['name'],
        address: data['address'],
        timezone: data['timezone'],
        createdAt: data['createdAt']?.toDate(),
      };
    });
  }

  async getLocation(locationId: string): Promise<Location | null> {
    const locRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/locations/${locationId}`
    );
    const snap = await getDoc(locRef);

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'],
      address: data['address'],
      timezone: data['timezone'],
      createdAt: data['createdAt']?.toDate(),
    };
  }
}
